// src/components/organisms/LiveResultsReport/LiveResultsReport.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip
} from 'chart.js';
import {
  Assessment as ReportIcon,
  Article as ArticleIcon,
  AutoAwesome as SummaryIcon,
  Campaign as SharedIcon,
  Insights as InsightsIcon,
  Language as SourceIcon,
  LocalOffer as TagIcon,
  Paid as ValueIcon,
  Public as ReachIcon,
  SentimentSatisfiedAlt as SentimentIcon,
  Share as ShareIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalElevation,
  operationalFontSizes,
  operationalRadii
} from '../../../design-system/tokens';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  ChartTooltip
);

const fallbackImage = 'https://media.mensuremidia.com.br/system/fallbacks/mensure-thumbnail.png';

const normalizeLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return (value.name || value.title || value.label || value.slug || '').toString().trim();
};

const getArticleImage = (article) => (
  article?.imageUrl || article?.image || article?.thumbnail || fallbackImage
);

const getArticleTags = (article) => (
  Array.isArray(article?.tags)
    ? article.tags.map(normalizeLabel).filter(Boolean)
    : []
);

const getReach = (article) => Number(
  article?.viewsNumber
  || article?.audience
  || article?.likesNumber
  || article?.domainInfo?.estimatedAudience
  || article?.monthlyAudience
  || 0
);

const getEngagement = (article) => Number(
  article?.engagement
  || article?.likesNumber
  || article?.commentsNumber
  || article?.sharesNumber
  || article?.reactionsNumber
  || 0
);

const getRelevanceScore = (article) => (
  getReach(article)
  + Number(article?.valuation || 0)
  + (getEngagement(article) * 10)
);

const getSentiment = (article) => (
  article?.sentimentAnalysis?.sentiment
  || article?.sentiment
  || article?.sentimento
  || 'neutral'
).toString().toLowerCase();

const getSource = (article) => (
  article?.siteName
  || article?.source
  || article?.vehicle
  || article?.mediaType
  || 'Fonte não identificada'
);

const formatCompact = (value) => {
  const number = Number(value || 0);
  if (!number) return '0';
  if (number >= 1000000) return `${(number / 1000000).toFixed(1).replace('.0', '')}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(1).replace('.0', '')}k`;
  return number.toLocaleString('pt-BR');
};

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (!number) return 'R$ 0';
  if (number >= 1000000) return `R$ ${(number / 1000000).toFixed(1).replace('.0', '')}M`;
  if (number >= 1000) return `R$ ${(number / 1000).toFixed(1).replace('.0', '')}k`;
  return `R$ ${number.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const countBy = (items, getValues) => {
  const map = new Map();

  items.forEach((item) => {
    const values = getValues(item);
    values.forEach((value) => {
      if (!value) return;
      map.set(value, (map.get(value) || 0) + 1);
    });
  });

  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
};

const getDayKey = (article) => {
  const date = article?.publishedAt || article?.date || article?.createdAt;
  if (!date) return 'Sem data';

  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC'
    });
  } catch {
    return 'Sem data';
  }
};

const MetricCard = ({ icon, label, value, helper }) => {
  const theme = useTheme();
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];

  return (
    <Box
      sx={{
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark'
          ? chipTokens.background
          : chipTokens.background,
        p: 1.5,
        minHeight: 112,
        display: 'grid',
        alignContent: 'space-between'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 850 }}>
          {label}
        </Typography>
        <Box sx={{ color: theme.palette.primary.main, display: 'grid', placeItems: 'center' }}>
          {icon}
        </Box>
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

MetricCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  helper: PropTypes.string
};

const ReportChartPanel = ({ title, icon, children, height = 260 }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        p: 1.5,
        display: 'grid',
        gap: 1.25,
        minHeight: height + 62
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ height }}>
        {children}
      </Box>
    </Box>
  );
};

ReportChartPanel.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  height: PropTypes.number
};

const ImpactArticle = ({ article, rank }) => {
  const theme = useTheme();
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];
  const valuation = Number(article?.valuation || 0);
  const reach = getReach(article);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '72px minmax(0, 1fr)',
        gap: 1.25,
        alignItems: 'center',
        p: 1,
        borderRadius: `${operationalRadii.surface}px`,
        bgcolor: theme.palette.mode === 'dark'
          ? chipTokens.background
          : chipTokens.background
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 72,
          height: 58,
          borderRadius: `${operationalRadii.control}px`,
          overflow: 'hidden',
          bgcolor: theme.palette.action.hover
        }}
      >
        <Box
          component="img"
          src={getArticleImage(article)}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            left: 5,
            minWidth: 22,
            height: 22,
            px: 0.5,
            borderRadius: `${operationalRadii.control}px`,
            bgcolor: alpha(theme.palette.common.black, 0.72),
            color: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            fontSize: operationalFontSizes.xs,
            fontWeight: 950
          }}
        >
          {rank}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {article?.title || 'Sem título'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {getSource(article)} · {formatCurrency(valuation)} · {formatCompact(reach)} alcance
        </Typography>
      </Box>
    </Box>
  );
};

ImpactArticle.propTypes = {
  article: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired
};

const SharedRelevanceArticle = ({ article, rank }) => {
  const theme = useTheme();
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];
  const valuation = Number(article?.valuation || 0);
  const reach = getReach(article);
  const engagement = getEngagement(article);
  const score = getRelevanceScore(article);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '88px minmax(0, 1fr)',
        gap: 1.25,
        alignItems: 'center',
        p: 1,
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark'
          ? chipTokens.background
          : chipTokens.background
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 88,
          height: 66,
          borderRadius: `${operationalRadii.control}px`,
          overflow: 'hidden',
          bgcolor: theme.palette.action.hover
        }}
      >
        <Box
          component="img"
          src={getArticleImage(article)}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            minWidth: 24,
            height: 24,
            px: 0.5,
            borderRadius: `${operationalRadii.control}px`,
            bgcolor: alpha(theme.palette.common.black, 0.76),
            color: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            fontSize: operationalFontSizes.xs,
            fontWeight: 950
          }}
        >
          {rank}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, display: 'grid', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 950, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {article?.title || 'Sem título'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {getSource(article)} · score {formatCompact(score)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${formatCompact(reach)} alcance`} sx={{ height: 23, borderRadius: `${operationalRadii.control}px`, fontWeight: 800 }} />
          <Chip size="small" label={formatCurrency(valuation)} sx={{ height: 23, borderRadius: `${operationalRadii.control}px`, fontWeight: 800 }} />
          <Chip size="small" label={`${formatCompact(engagement)} eng.`} sx={{ height: 23, borderRadius: `${operationalRadii.control}px`, fontWeight: 800 }} />
        </Box>
      </Box>
    </Box>
  );
};

SharedRelevanceArticle.propTypes = {
  article: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired
};

const ExecutiveSummary = ({ metrics }) => {
  const theme = useTheme();
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];
  const topSource = metrics.sourceItems[0]?.label || 'fontes monitoradas';
  const topTag = metrics.tagItems[0]?.label || 'temas do recorte';
  const topArticle = metrics.topArticles[0]?.title || 'principal artigo do recorte';
  const reachLabel = formatCompact(metrics.totalReach);
  const valuationLabel = formatCurrency(metrics.totalValuation);
  const hasPositiveReading = metrics.positiveRate >= 50;
  const narrative = `O recorte analisado reúne ${metrics.totalArticles} artigo${metrics.totalArticles !== 1 ? 's' : ''}, com alcance estimado de ${reachLabel} e valoração consolidada de ${valuationLabel}. A cobertura aparece concentrada em ${topSource}, com maior recorrência temática em ${topTag}. O artigo de maior impacto no período é "${topArticle}".`;

  const insights = [
    {
      label: 'Leitura geral',
      text: hasPositiveReading
        ? `Predomínio favorável: ${metrics.positiveRate}% do recorte está positivo.`
        : `A leitura pede atenção: o positivo representa ${metrics.positiveRate}% do recorte.`
    },
    {
      label: 'Concentração',
      text: `${topSource} lidera entre as fontes e pode orientar a leitura do resultado.`
    },
    {
      label: 'Tema-chave',
      text: `${topTag} é o marcador mais recorrente entre os artigos analisados.`
    }
  ];

  return (
    <Box
      sx={{
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark'
          ? chipTokens.background
          : chipTokens.background,
        p: { xs: 1.5, md: 2 },
        display: 'grid',
        gap: 1.5
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SummaryIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
          Sumário executivo
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.primary' }}>
        {narrative}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1
        }}
      >
        {insights.map((item) => (
          <Box
            key={item.label}
            sx={{
              borderRadius: `${operationalRadii.control}px`,
              border: `1px solid ${theme.palette.divider}`,
              p: 1.25,
              bgcolor: theme.palette.mode === 'dark'
                ? operationalComponentSurfaces.filterPanel.dark
                : operationalComponentSurfaces.filterPanel.light
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 750, lineHeight: 1.45 }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

ExecutiveSummary.propTypes = {
  metrics: PropTypes.shape({
    totalArticles: PropTypes.number.isRequired,
    totalReach: PropTypes.number.isRequired,
    totalValuation: PropTypes.number.isRequired,
    positiveRate: PropTypes.number.isRequired,
    sourceItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    tagItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    topArticles: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired
};

const LiveResultsReport = ({ articles = [], title = 'Relatório vivo', brandName }) => {
  const theme = useTheme();

  const metrics = React.useMemo(() => {
    const totalArticles = articles.length;
    const totalReach = articles.reduce((sum, article) => sum + getReach(article), 0);
    const totalValuation = articles.reduce((sum, article) => sum + Number(article?.valuation || 0), 0);
    const sourceItems = countBy(articles, article => [getSource(article)]).slice(0, 5);
    const tagItems = countBy(articles, getArticleTags).slice(0, 6);
    const timelineItems = countBy(articles, article => [getDayKey(article)]).slice(0, 7).reverse();
    const sentimentItems = countBy(articles, article => [getSentiment(article)]);
    const topArticles = [...articles]
      .sort((a, b) => (Number(b?.valuation || 0) + getReach(b)) - (Number(a?.valuation || 0) + getReach(a)))
      .slice(0, 4);
    const sharedRelevantArticles = [...articles]
      .sort((a, b) => getRelevanceScore(b) - getRelevanceScore(a))
      .slice(0, 4);
    const positiveCount = sentimentItems.find(item => ['positive', 'positivo'].includes(item.label))?.count || 0;
    const dominantSentiment = sentimentItems[0]?.label || 'neutral';

    return {
      totalArticles,
      totalReach,
      totalValuation,
      sourceItems,
      tagItems,
      timelineItems,
      sentimentItems,
      topArticles,
      sharedRelevantArticles,
      positiveRate: totalArticles ? Math.round((positiveCount / totalArticles) * 100) : 0,
      dominantSentiment
    };
  }, [articles]);

  const hasData = metrics.totalArticles > 0;
  const chartTextColor = theme.palette.text.secondary;
  const chartGridColor = theme.palette.divider;
  const chartOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: chartTextColor,
          boxWidth: 12,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label ? `${context.dataset.label}: ` : '';
            return `${label}${context.raw ?? context.parsed?.y ?? 0}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: chartTextColor },
        grid: { color: chartGridColor }
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, precision: 0 },
        grid: { color: chartGridColor }
      }
    }
  }), [chartGridColor, chartTextColor]);
  const pieChartOptions = React.useMemo(() => ({
    ...chartOptions,
    scales: undefined
  }), [chartOptions]);
  const timelineChartData = React.useMemo(() => ({
    labels: metrics.timelineItems.map(item => item.label),
    datasets: [{
      label: 'Artigos por dia',
      data: metrics.timelineItems.map(item => item.count),
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
      fill: true,
      tension: 0.4
    }]
  }), [metrics.timelineItems, theme.palette.primary.main]);
  const sentimentChartData = React.useMemo(() => ({
    labels: metrics.sentimentItems.map(item => item.label),
    datasets: [{
      data: metrics.sentimentItems.map(item => item.count),
      backgroundColor: [
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main
      ]
    }]
  }), [metrics.sentimentItems, theme.palette.error.main, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main]);
  const sourceChartData = React.useMemo(() => ({
    labels: metrics.sourceItems.map(item => item.label),
    datasets: [{
      label: 'Artigos por fonte',
      data: metrics.sourceItems.map(item => item.count),
      backgroundColor: theme.palette.info.main
    }]
  }), [metrics.sourceItems, theme.palette.info.main]);
  const tagChartData = React.useMemo(() => ({
    labels: metrics.tagItems.map(item => item.label),
    datasets: [{
      label: 'Artigos por tag',
      data: metrics.tagItems.map(item => item.count),
      backgroundColor: theme.palette.secondary.main
    }]
  }), [metrics.tagItems, theme.palette.secondary.main]);

  return (
    <Box
      sx={{
        borderRadius: `${operationalRadii.surface}px`,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark'
          ? operationalComponentSurfaces.filterPanel.dark
          : operationalComponentSurfaces.filterPanel.light,
        p: { xs: 2, md: 2.5 },
        display: 'grid',
        gap: 2.25,
        boxShadow: theme.palette.mode === 'dark'
          ? operationalElevation.raised
          : operationalElevation.surface
      }}
    >
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ReportIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 950 }}>
              {title}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Protótipo de página de resultado com KPIs, gráficos e artigos relacionados{brandName ? ` para ${brandName}` : ''}.
          </Typography>
        </Box>
        <Chip
          icon={<ShareIcon />}
          label="Prévia de página pública"
          color="primary"
          variant="outlined"
          sx={{ borderRadius: `${operationalRadii.control}px`, fontWeight: 850 }}
        />
      </Box>

      {!hasData ? (
        <Box sx={{ p: 2, borderRadius: `${operationalRadii.surface}px`, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            Carregue artigos para visualizar o relatório.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))'
              },
              gap: 1.25
            }}
          >
            <MetricCard
              icon={<ArticleIcon fontSize="small" />}
              label="Artigos analisados"
              value={formatCompact(metrics.totalArticles)}
              helper="Base carregada no teste"
            />
            <MetricCard
              icon={<ReachIcon fontSize="small" />}
              label="Alcance estimado"
              value={formatCompact(metrics.totalReach)}
              helper={`${metrics.sourceItems.length} fontes principais`}
            />
            <MetricCard
              icon={<ValueIcon fontSize="small" />}
              label="Valoração"
              value={formatCurrency(metrics.totalValuation)}
              helper="Soma do recorte"
            />
            <MetricCard
              icon={<SentimentIcon fontSize="small" />}
              label="Sentimento"
              value={`${metrics.positiveRate}% positivo`}
              helper={`Predominante: ${metrics.dominantSentiment}`}
            />
          </Box>

          <ExecutiveSummary metrics={metrics} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' },
              gap: 2
            }}
          >
            <ReportChartPanel
              title="Evolução por dia"
              icon={<TrendingIcon fontSize="small" color="primary" />}
              height={300}
            >
              <Line data={timelineChartData} options={chartOptions} />
            </ReportChartPanel>

            <ReportChartPanel
              title="Sentimento"
              icon={<InsightsIcon fontSize="small" color="primary" />}
              height={300}
            >
              <Pie data={sentimentChartData} options={pieChartOptions} />
            </ReportChartPanel>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 2
            }}
          >
            <ReportChartPanel
              title="Top fontes"
              icon={<SourceIcon fontSize="small" color="primary" />}
              height={270}
            >
              <Bar data={sourceChartData} options={chartOptions} />
            </ReportChartPanel>

            <ReportChartPanel
              title="Tags relacionadas"
              icon={<TagIcon fontSize="small" color="primary" />}
              height={270}
            >
              <Bar data={tagChartData} options={chartOptions} />
            </ReportChartPanel>
          </Box>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsightsIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                Artigos de maior impacto
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25
              }}
            >
              {metrics.topArticles.map((article, index) => (
                <ImpactArticle
                  article={article}
                  rank={index + 1}
                  key={article?._id || `${article?.title}-${index}`}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SharedIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Conteúdos compartilhados de maior relevância
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                Critério: alcance + valoração + engajamento disponível
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25
              }}
            >
              {metrics.sharedRelevantArticles.map((article, index) => (
                <SharedRelevanceArticle
                  article={article}
                  rank={index + 1}
                  key={article?._id || `${article?.title}-shared-${index}`}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

LiveResultsReport.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  brandName: PropTypes.string
};

export default LiveResultsReport;
